import React, { useState } from 'react';
import useSpeechSynthesis from '../hooks/useSpeechSynthesis';
import '../style/components/_speechSynthesis.scss';

const SpeechButton = ({ 
  text, 
  elementId, 
  readFullPage = false,
  className = "speech-button",
  children,
  options = {}
}) => {
  const { speak, speakElement, speakPage, stop, speaking, supported, voices } = useSpeechSynthesis();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [volume, setVolume] = useState(1);

  const handlePlay = () => {
    const speechOptions = {
      ...options,
      voice: selectedVoice,
      rate,
      pitch,
      volume,
      onEnd: () => setIsOpen(false)
    };

    if (readFullPage) {
      speakPage(speechOptions);
    } else if (elementId) {
      speakElement(elementId, speechOptions);
    } else if (text) {
      speak(text, speechOptions);
    }
    setIsOpen(true);
  };

  const handleStop = () => {
    stop();
    setIsOpen(false);
  };

  const toggleOpen = () => {
    if (!speaking) {
      setIsOpen(!isOpen);
    }
  };

  if (!supported) {
    return null;
  }

  // Filtrer les voix françaises
  const frenchVoices = voices.filter(voice => 
    voice.lang.startsWith('fr') || voice.name.toLowerCase().includes('french')
  );

  return (
    <>
      {/* Bouton toggle (visible uniquement quand pas de lecture) */}
      {!speaking && (
        <button 
          className={`speech-button-fixed-toggle ${isOpen ? 'hidden' : ''}`}
          onClick={toggleOpen}
          type="button"
          aria-label="Ouvrir les options de lecture"
          tabIndex={0}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{display: 'block', margin: 'auto'}}>
            <path d="M3 10v4c0 .55.45 1 1 1h3l3.29 3.29c.63.63 1.71.18 1.71-.71V6.41c0-.89-1.08-1.34-1.71-.71L7 9H4c-.55 0-1 .45-1 1z"/>
            <path d="M16.5 12A4.5 4.5 0 0014 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
            <path d="M19 12c0-2.53-1.4-4.84-3.65-6.05-.34-.18-.76-.01-.85.38-.09.39.14.77.51.88C16.36 8.16 17 10 17 12s-.64 3.84-2 4.79c-.37.11-.6.49-.51.88.09.39.51.56.85.38C17.6 16.84 19 14.53 19 12z"/>
          </svg>
        </button>
      )}

      {/* Conteneur principal */}
      <div className={`speech-button-container ${isOpen || speaking ? 'open' : ''}`}>
        <div className="speech-controls">
          <div className="speech-buttons-group">
            {/* Bouton lecture (visible uniquement quand pas de lecture) */}
            {!speaking && (
              <button 
                className={className}
                onClick={handlePlay}
                type="button"
                aria-label="Commencer la lecture"
                tabIndex={0}
              >
                <span className="speech-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{display: 'block', margin: 'auto'}}>
                    <path d="M8 6.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18c.62-.39.62-1.29 0-1.68L9.54 5.98C8.87 5.55 8 6.03 8 6.82z"/>
                  </svg>
                </span>
                <span className="speech-text">Écouter</span>
              </button>
            )}

            {/* Bouton stop (visible uniquement pendant la lecture) */}
            {speaking && (
              <button
                className="speech-button speech-button-stop"
                onClick={handleStop}
                type="button"
                aria-label="Arrêter la lecture"
                tabIndex={0}
              >
                <span className="speech-icon" aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{display: 'block', margin: 'auto'}}>
                    <path d="M6 6h12v12H6z"/>
                  </svg>
                </span>
                <span className="speech-text">Stop</span>
              </button>
            )}
          </div>
          
          {/* Bouton fermer (visible uniquement quand pas de lecture) */}
          {!speaking && (
            <button
              className="speech-button-close"
              onClick={toggleOpen}
              type="button"
              aria-label="Fermer les options de lecture"
              tabIndex={0}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{display: 'block', margin: 'auto'}}>
                <path d="M18.3 5.71c-.39-.39-1.02-.39-1.41 0L12 10.59 7.11 5.7c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L10.59 12 5.7 16.89c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L12 13.41l4.89 4.89c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L13.41 12l4.89-4.89c.38-.38.38-1.02 0-1.4z"/>
              </svg>
            </button>
          )}

          {/* Options de lecture */}
          <div className="speech-settings">
            {/* Sélecteur de voix */}
            {frenchVoices.length > 0 && (
              <div className="setting-group">
                <label htmlFor="voice-select">
                  Voix : <span className="setting-value">
                    {selectedVoice?.name 
                      ? (selectedVoice.name.length > 20 ? selectedVoice.name.substring(0, 20) + '...' : selectedVoice.name)
                      : 'Par défaut'
                    }
                  </span>
                </label>
                <select 
                  id="voice-select"
                  value={selectedVoice?.name || ''}
                  onChange={(e) => {
                    const voice = frenchVoices.find(v => v.name === e.target.value);
                    setSelectedVoice(voice);
                  }}
                  disabled={speaking}
                  aria-label="Sélectionner une voix pour la lecture"
                  tabIndex={0}
                >
                  <option value="">Voix par défaut</option>
                  {frenchVoices.map((voice, index) => (
                    <option key={index} value={voice.name}>
                      {voice.name.length > 35 ? voice.name.substring(0, 35) + '...' : voice.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Contrôle de la vitesse */}
            <div className="setting-group">
              <label htmlFor="rate-control">
                Vitesse : <span className="setting-value">{rate}x</span>
              </label>
              <input 
                id="rate-control"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                disabled={speaking}
                aria-label={`Vitesse de lecture: ${rate}x`}
                tabIndex={0}
              />
            </div>

            {/* Contrôle de la hauteur */}
            <div className="setting-group">
              <label htmlFor="pitch-control">
                Hauteur : <span className="setting-value">{pitch}</span>
              </label>
              <input 
                id="pitch-control"
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                disabled={speaking}
                aria-label={`Hauteur de la voix: ${pitch}`}
                tabIndex={0}
              />
            </div>

            {/* Contrôle du volume */}
            <div className="setting-group">
              <label htmlFor="volume-control">
                Volume : <span className="setting-value">{Math.round(volume * 100)}%</span>
              </label>
              <input 
                id="volume-control"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                disabled={speaking}
                aria-label={`Volume: ${Math.round(volume * 100)}%`}
                tabIndex={0}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SpeechButton; 